import React, { useEffect, useRef, useState } from 'react';
import { MdVerified, MdSms } from 'react-icons/md';
import api from './api';
import { normalizePhone, formatPhone, phoneProblem, countryOf } from './phone';
import CountryFlag from './CountryFlag';
import './phoneInput.css';

/**
 * PhoneInput — one phone box for the whole app, any country.
 *
 *   <PhoneInput value={form.father_phone} onChange={(v) => setForm(...)} />
 *
 * The number is typed with its country code ("+49 1512 3456789",
 * "+963 933 123 456"). The two local habits of this school are also
 * understood: "0933…" becomes +963, "05…" becomes +90. On leaving the
 * box it is shown tidied ("+963 933 123 456"), and the form receives the
 * saved form "+963933123456". A number that cannot be a phone is passed
 * on as typed, so the server refuses it with a clear message.
 *
 * Verification is optional and never blocks saving: under a valid
 * number a small «تحقق» link sends a code to that phone; typing the code
 * marks it ✓ موثّق. A number verified before shows ✓ straight away.
 */
export default function PhoneInput({ value, onChange, verify = true, disabled = false, autoFocus = false }) {
  const [text, setText] = useState(() => (value ? formatPhone(value) : ''));
  const [touched, setTouched] = useState(false);

  // Follow outside changes (form reset, editing another record).
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (value === lastEmitted.current) return;
    setText(value ? formatPhone(value) : '');
    setTouched(false);
    lastEmitted.current = value;
  }, [value]);

  const onType = (e) => {
    const raw = e.target.value.replace(/[^\d+\s\-()]/g, '');
    setText(raw);
    const n = normalizePhone(raw);
    const out = raw.trim() ? (n || raw.trim()) : '';
    lastEmitted.current = out;
    onChange(out);
  };

  const onBlur = () => {
    setTouched(true);
    const n = normalizePhone(text);
    if (n) setText(formatPhone(n));
  };

  const full = normalizePhone(text);
  const problem = text.trim() ? phoneProblem(text) : '';
  const country = full ? countryOf(full) : null;

  return (
    <div className="ui-phone">
      <input
        className={`ui-input ui-phone-number${touched && problem ? ' has-error' : ''}`}
        dir="ltr"
        inputMode="tel"
        autoComplete="tel"
        value={text}
        onChange={onType}
        onBlur={onBlur}
        placeholder="+963 933 123 456"
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {touched && problem && <div className="ui-phone-msg is-error">{problem}</div>}
      {!problem && full && country && <div className="ui-phone-msg"><CountryFlag country={country} /> {country.name}</div>}
      {verify && full && !problem && !disabled && <VerifyBox phone={full} />}
    </div>
  );
}

function VerifyBox({ phone }) {
  const [state, setState] = useState('idle'); // idle | sending | code | checking | verified
  const [digits, setDigits] = useState('');
  const [msg, setMsg] = useState('');
  const [hint, setHint] = useState('');

  // Already verified before? One small request per settled number.
  useEffect(() => {
    let off = false;
    setState('idle'); setDigits(''); setMsg(''); setHint('');
    const t = setTimeout(() => {
      api.phoneVerifyStatus([phone])
        .then((r) => { if (!off && r?.[phone]) setState('verified'); })
        .catch(() => {});
    }, 400);
    return () => { off = true; clearTimeout(t); };
  }, [phone]);

  const send = async () => {
    setState('sending'); setMsg('');
    try {
      const r = await api.phoneVerifySend(phone);
      setHint(r.dev_code ? `رمز التطوير: ${r.dev_code}` : '');
      setState('code');
    } catch (e) {
      setMsg(e.message); setState('idle');
    }
  };

  const check = async () => {
    if (digits.length < 4) return;
    setState('checking'); setMsg('');
    try {
      await api.phoneVerifyCheck(phone, digits);
      setState('verified');
    } catch (e) {
      setMsg(e.message); setState('code');
    }
  };

  if (state === 'verified') {
    return <div className="ui-phone-msg is-ok"><MdVerified size={15} /> رقم موثّق</div>;
  }
  if (state === 'code' || state === 'checking') {
    return (
      <div className="ui-phone-verify">
        <span>أدخل الرمز المرسل إلى الهاتف</span>
        <input
          className="ui-input ui-phone-code"
          dir="ltr"
          inputMode="numeric"
          maxLength={6}
          value={digits}
          onChange={(e) => setDigits(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } }}
          autoFocus
          placeholder="000000"
        />
        <button type="button" className="ui-btn-mini" onClick={check} disabled={state === 'checking' || digits.length < 4}>
          {state === 'checking' ? '…' : 'تأكيد'}
        </button>
        <button type="button" className="ui-phone-link" onClick={() => setState('idle')}>لاحقاً</button>
        {hint && <small className="ui-phone-hint">{hint}</small>}
        {msg && <small className="ui-phone-err">{msg}</small>}
      </div>
    );
  }
  return (
    <div className="ui-phone-msg">
      <button type="button" className="ui-phone-link" onClick={send} disabled={state === 'sending'}>
        <MdSms size={14} /> {state === 'sending' ? 'جارٍ الإرسال…' : 'تحقق من الرقم برسالة (اختياري)'}
      </button>
      {msg && <small className="ui-phone-err">{msg}</small>}
    </div>
  );
}
